import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { ConsultButton, WhatsAppLink, CallLink } from "@/components/CtaButtons";
import { useLanguage } from "@/i18n/LanguageContext";
import { IMAGES } from "@/data/images";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden bg-ink">
      <motion.div className="absolute inset-0" style={reduce ? undefined : { y, scale }}>
        <img
          src={IMAGES.hero1}
          alt="Premium modern villa — residential construction by D & N Constructions"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/35" />

      <div className="relative mx-auto w-full max-w-7xl px-5 pb-32 pt-40 md:px-8">
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          className="mb-8 h-px w-28 origin-left bg-gold"
        />
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="eyebrow-dark"
        >
          {t("hero.eyebrow")}
        </motion.p>
        <h1 className="mt-6 font-display text-[13vw] leading-[0.98] tracking-tight text-offwhite sm:text-6xl md:text-7xl lg:text-8xl">
          <MaskedLine delay={0.45}>{t("hero.h1a")}</MaskedLine>
          <MaskedLine delay={0.62}>
            <span className="italic text-gold">{t("hero.h1b")}</span>
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/75 md:text-lg"
        >
          {t("hero.sub")}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <ConsultButton testid="hero-consult-button" />
          <Link
            to="/projects"
            data-testid="hero-explore-button"
            className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold text-offwhite transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold hover:bg-white/5"
          >
            {t("cta.explore")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.35 }}
          className="mt-8 flex flex-wrap items-center gap-6"
        >
          <CallLink testid="hero-call-link" className="text-white/80" />
          <span className="h-4 w-px bg-white/20" aria-hidden="true" />
          <WhatsAppLink testid="hero-whatsapp-link" className="border-none px-0 py-0 text-white/80" />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
        aria-hidden="true"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">{t("hero.scroll")}</span>
        <motion.span animate={reduce ? undefined : { y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown className="h-4 w-4 text-gold" />
        </motion.span>
      </motion.div>
    </section>
  );
};
